import React, { useCallback, useState } from 'react';
import { Report } from 'shared/components/WelcomeBack/utils';
import styled from 'styled-components';
import { Answer } from '../Review/utils';
import TrueImpactModal, {
  modalHeightAutoStyles,
} from 'shared/design-system/components/TrueImpactModal';
import SelectQuestion from 'admin/pages/ManageAccounts/CustomQuestions/CustomQuestionDetails/CustomQuestionTypes/SelectQuestion';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { updateAnswer } from '../../../NPOFeedback/MakeEdits/api';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { AllNpoReportQuestions } from 'admin/pages/ManageAccounts/Guidance/api';

interface AddAnswerModalProps {
  open: boolean;
  report: Report;
  programName: string;
  isReportPassed: boolean;
  handleClose: () => unknown;
  setAnswers: React.Dispatch<React.SetStateAction<Answer[]>>;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.extraLarge};
  textarea {
    background-color: ${COLORS.whiteBase};
  }
`;

type Question = AllNpoReportQuestions['allNpoReportQuestions'][number];
const AddAnswerModal: React.FC<AddAnswerModalProps> = ({
  open,
  report,
  programName,
  isReportPassed,
  handleClose,
  setAnswers,
}) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest<unknown>();
  const [text, setText] = useState<string>('');
  const [question, setQuestion] = useState<Question | undefined>(undefined);

  const handleSubmit = useCallback(() => {
    const questionId = question?.dbId;
    if (!text || !questionId) return;
    const request = updateAnswer(client!, {
      reportId: report.dbId,
      questionId: questionId,
      answerId: undefined,
      ignoreClearData: true,
      answer: text,
    });
    makeRequest(request);
    request.then(r => {
      if (r.errors || !r.data || !r.data.newNpoAnswer.success) return;
      setAnswers(s => {
        const newState = [...s];
        const existingAnswerIndex = newState.findIndex(
          x => x.questionId === questionId,
        );
        if (existingAnswerIndex === -1) {
          newState.push({
            answerValidations: [],
            text: text,
            date1: null,
            date2: null,
            enumResponse: null,
            autoAnswered: false,
            questionId: questionId,
            dbId: r.data.newNpoAnswer.id,
            question: {
              dbId: questionId,
              text: question.text,
              section: question.section,
              questionType: question.questionType,
              nextQuestionId: question.nextQuestionId,
              enumResponses: [],
            },
          });
        } else {
          newState[existingAnswerIndex].text = text;
        }
        return newState;
      });
      handleClose();
    });
  }, [text, question]);

  return (
    <TrueImpactModal
      open={open}
      maxWidth="800px"
      onClose={handleClose}
      next={{
        text: 'Save Answer',
        onClick: handleSubmit,
        disabled: !text || !question,
      }}
      addStyles={modalHeightAutoStyles}
    >
      <StyledDiv>
        <SelectQuestion
          forceAllowQuestionIds={[50002]}
          selectQuestionId={question?.dbId}
          disableOtherSections={false}
          handleChange={setQuestion}
        />
        <TrueImpactTextfield
          placeholder="Answer"
          isTextarea={true}
          value={text}
          onChange={setText}
        />
      </StyledDiv>
    </TrueImpactModal>
  );
};

export default AddAnswerModal;
